/**
 * ApiLogsTab Component
 *
 * Displays API request logs in a DataTable with search, filters, and live updates
 */

import { useState, useMemo, useCallback } from 'react';
import { IconWifi, IconSearch, IconFilter, IconEye, IconX } from '@tabler/icons-react';
import { Stack, Card, Group, Button, Select, MultiSelect, TextInput, Badge, Text, Code, ActionIcon, Modal, Divider, ScrollArea } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { ApiLogsLiveModal } from './LiveUpdatesModal';
import type { ApiLogsTabProps, ApiLogRow } from '../types';
import type { ApiRequestLogDto } from '@actschurch/ccms-sdk';

// Helper: Format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Helper: Format duration
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

// Helper: Get HTTP method color (Swagger style)
const getHttpMethodColor = (method: string): string => {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'blue';      // Swagger GET - blue
    case 'POST':
      return 'green';     // Swagger POST - green
    case 'PUT':
      return 'orange';    // Swagger PUT - orange
    case 'DELETE':
      return 'red';       // Swagger DELETE - red
    case 'PATCH':
      return 'teal';      // Swagger PATCH - teal
    case 'OPTIONS':
      return 'violet';    // Swagger OPTIONS - violet
    case 'HEAD':
      return 'grape';     // Swagger HEAD - grape
    default:
      return 'gray';
  }
};

// Helper: Get status code color with distinct colors for each status range
const getStatusCodeColor = (code: number): string => {
  // 2xx Success - shades of green
  if (code === 200) return 'lime';       // OK
  if (code === 201) return 'teal';       // Created
  if (code === 204) return 'green';      // No Content
  if (code >= 200 && code < 300) return 'cyan';  // Other 2xx

  // 3xx Redirect - shades of blue
  if (code === 301) return 'indigo';     // Moved Permanently
  if (code === 302) return 'blue';       // Found
  if (code >= 300 && code < 400) return 'violet';  // Other 3xx

  // 4xx Client Error - shades of yellow/orange
  if (code === 400) return 'yellow';     // Bad Request
  if (code === 401) return 'orange';     // Unauthorized
  if (code === 403) return 'orange';     // Forbidden
  if (code === 404) return 'yellow';     // Not Found
  if (code >= 400 && code < 500) return 'orange';  // Other 4xx

  // 5xx Server Error - shades of red
  if (code === 500) return 'red';        // Internal Server Error
  if (code === 502) return 'pink';       // Bad Gateway
  if (code === 503) return 'red';        // Service Unavailable
  if (code >= 500) return 'red';         // Other 5xx

  return 'gray';
};

export function ApiLogsTab({ logs, onRefresh, loading }: ApiLogsTabProps) {
  const [liveModalOpened, setLiveModalOpened] = useState(false);
  const [searchPath, setSearchPath] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterServices, setFilterServices] = useState<string[]>([]);
  const [selectedLog, setSelectedLog] = useState<ApiRequestLogDto | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Get unique services for filter dropdown
  const services = useMemo(() => {
    const serviceSet = new Set<string>();
    logs.forEach((log) => {
      if (log.serviceName) serviceSet.add(log.serviceName);
    });
    return Array.from(serviceSet).sort();
  }, [logs]);

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Method filter
      if (filterMethod && log.httpMethod !== filterMethod) return false;

      // Status code filter
      if (filterStatus) {
        const statusRange = parseInt(filterStatus);
        if (log.statusCode < statusRange || log.statusCode >= statusRange + 100) return false;
      }

      // Service filter (multi-select)
      if (filterServices.length > 0 && !filterServices.includes(log.serviceName || '')) return false;

      // Path search
      if (searchPath && !log.path.toLowerCase().includes(searchPath.toLowerCase())) return false;

      return true;
    });
  }, [logs, filterMethod, filterStatus, filterServices, searchPath]);

  // Convert to row format
  const rows: ApiLogRow[] = useMemo(() => {
    return filteredLogs.map((log) => ({
      ...log,
      id: log.id,
    }));
  }, [filteredLogs]);

  // Handle view details
  const handleViewDetails = useCallback((log: ApiRequestLogDto) => {
    setSelectedLog(log);
  }, []);

  // Define columns
  const columns: DataTableColumn<ApiLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        width: '180px',
        render: (value) => (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {formatTimestamp(value as string)}
          </Text>
        ),
      },
      {
        key: 'httpMethod',
        label: 'Method',
        width: '100px',
        render: (value) => (
          <Badge color={getHttpMethodColor(value as string)} size="sm" variant="filled" style={{ minWidth: '70px', textAlign: 'center' }}>
            {value as string}
          </Badge>
        ),
      },
      {
        key: 'path',
        label: 'Path',
        render: (value) => (
          <Text size="sm" lineClamp={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value as string}
          </Text>
        ),
      },
      {
        key: 'statusCode',
        label: 'Status',
        width: '90px',
        render: (value) => (
          <Badge color={getStatusCodeColor(value as number)} size="sm" variant="filled" style={{ minWidth: '60px', textAlign: 'center' }}>
            {value as number}
          </Badge>
        ),
      },
      {
        key: 'durationMs',
        label: 'Duration',
        sortable: true,
        width: '100px',
        render: (value) => {
          const duration = value as number;
          return (
            <Badge
              size="sm"
              color={duration > 2000 ? 'red' : duration > 1000 ? 'orange' : duration > 500 ? 'yellow' : 'lime'}
              variant="light"
              style={{ minWidth: '70px', textAlign: 'center' }}
            >
              {formatDuration(duration)}
            </Badge>
          );
        },
      },
      {
        key: 'clientIp',
        label: 'Client IP',
        width: '130px',
        render: (value) => (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {(value as string) || 'N/A'}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: '60px',
        render: (_, row) => (
          <ActionIcon
            variant="light"
            size="sm"
            color="blue"
            onClick={() => handleViewDetails(row)}
            aria-label="View request details"
          >
            <IconEye size={14} />
          </ActionIcon>
        ),
      },
    ],
    [handleViewDetails]
  );

  return (
    <Stack gap="md">
      {/* Actions and Filters */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Group gap="md">
            <Button
              leftSection={<IconWifi size={16} />}
              onClick={() => setLiveModalOpened(true)}
              variant="gradient"
              gradientVariant="primary"
            >
              Live Updates
            </Button>
            <Button
              variant="light"
              onClick={onRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>

          <Group gap="md">
            <Select
              placeholder="HTTP Method"
              value={filterMethod}
              onChange={(value) => setFilterMethod(value || '')}
              data={[
                { value: '', label: 'All Methods' },
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
                { value: 'PATCH', label: 'PATCH' },
              ]}
              leftSection={<IconFilter size={16} />}
              style={{ minWidth: 150 }}
            />
            <Select
              placeholder="Status Code"
              value={filterStatus}
              onChange={(value) => setFilterStatus(value || '')}
              data={[
                { value: '', label: 'All Status' },
                { value: '200', label: '2xx Success' },
                { value: '300', label: '3xx Redirect' },
                { value: '400', label: '4xx Client Error' },
                { value: '500', label: '5xx Server Error' },
              ]}
              leftSection={<IconFilter size={16} />}
              style={{ minWidth: 150 }}
            />
            <MultiSelect
              placeholder="Filter by service"
              value={filterServices}
              onChange={setFilterServices}
              data={services.map((s) => ({ value: s, label: s }))}
              leftSection={<IconFilter size={16} />}
              clearable
              style={{ minWidth: 280 }}
            />
            <TextInput
              placeholder="Search path..."
              value={searchPath}
              onChange={(e) => setSearchPath(e.target.value)}
              leftSection={<IconSearch size={16} />}
              style={{ flex: 1 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* DataTable */}
      <Card padding="lg">
        <DataTable<ApiLogRow>
          data={rows}
          columns={columns}
          pagination={{
            page,
            pageSize,
            total: rows.length,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          gradientVariant="primary"
          highlightOnHover
          striped
          stickyHeader
          withRowAnimations
          emptyMessage="No API request logs found. Try adjusting your filters."
          enableInternalSort
          defaultSortKey="timestamp"
          defaultSortDirection="desc"
        />
      </Card>

      <Text size="sm" c="dimmed">
        Showing {rows.length} of {logs.length} requests
      </Text>

      {/* Live Updates Modal */}
      <ApiLogsLiveModal
        opened={liveModalOpened}
        onClose={() => setLiveModalOpened(false)}
        serviceNames={filterServices.length > 0 ? filterServices : undefined}
      />

      {/* API Request Detail Modal */}
      <Modal
        opened={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="API Request Details"
        size="lg"
        variant="elevated"
      >
        {selectedLog && (
          <Stack gap="md">
            {/* Header Info */}
            <Group gap="sm">
              <Badge color={getHttpMethodColor(selectedLog.httpMethod)} size="md" variant="filled">
                {selectedLog.httpMethod}
              </Badge>
              <Badge color={getStatusCodeColor(selectedLog.statusCode)} size="md" variant="filled">
                {selectedLog.statusCode}
              </Badge>
              <Text size="sm" c="dimmed">
                {formatTimestamp(selectedLog.timestamp)}
              </Text>
              <Badge
                size="md"
                color={selectedLog.durationMs > 2000 ? 'red' : selectedLog.durationMs > 1000 ? 'orange' : selectedLog.durationMs > 500 ? 'yellow' : 'lime'}
                variant="light"
              >
                {formatDuration(selectedLog.durationMs)}
              </Badge>
            </Group>

            <Divider />

            {/* Path */}
            <div>
              <Text size="xs" c="dimmed" fw={600} mb="xs">
                Path
              </Text>
              <Card withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}>
                <Code size="sm" style={{ wordBreak: 'break-all' }}>
                  {selectedLog.path}
                </Code>
              </Card>
            </div>

            {/* Client Info */}
            <Group gap="xl">
              {selectedLog.clientIp && (
                <div>
                  <Text size="xs" c="dimmed" fw={600}>
                    Client IP
                  </Text>
                  <Text size="sm">{selectedLog.clientIp}</Text>
                </div>
              )}
              {selectedLog.userAgent && (
                <div>
                  <Text size="xs" c="dimmed" fw={600}>
                    User Agent
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {selectedLog.userAgent}
                  </Text>
                </div>
              )}
            </Group>

            {/* Query String */}
            {selectedLog.queryString && (
              <>
                <Divider />
                <div>
                  <Text size="xs" c="dimmed" fw={600} mb="xs">
                    Query String
                  </Text>
                  <Card withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}>
                    <Code size="sm" block>
                      {selectedLog.queryString}
                    </Code>
                  </Card>
                </div>
              </>
            )}

            {/* Request Body */}
            {selectedLog.requestBody && (
              <>
                <Divider />
                <div>
                  <Text size="xs" c="dimmed" fw={600} mb="xs">
                    Request Body
                  </Text>
                  <ScrollArea h={200}>
                    <Card withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-blue-light)' }}>
                      <Code block style={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {selectedLog.requestBody}
                      </Code>
                    </Card>
                  </ScrollArea>
                </div>
              </>
            )}

            {/* Response Body */}
            {selectedLog.responseBody && (
              <>
                <Divider />
                <div>
                  <Text size="xs" c="dimmed" fw={600} mb="xs">
                    Response Body
                  </Text>
                  <ScrollArea h={200}>
                    <Card
                      withBorder
                      p="md"
                      style={{
                        backgroundColor:
                          selectedLog.statusCode >= 400
                            ? 'var(--mantine-color-red-light)'
                            : 'var(--mantine-color-green-light)',
                      }}
                    >
                      <Code block style={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {selectedLog.responseBody}
                      </Code>
                    </Card>
                  </ScrollArea>
                </div>
              </>
            )}

            {/* Close Button */}
            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={() => setSelectedLog(null)}
                leftSection={<IconX size={16} />}
              >
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
